import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { Navigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import ProtectedRoute from '../components/auth/ProtectedRoute'
import { AppHeader } from '../components'
import UserManagement from '../components/admin/UserManagement'
import XLSXImport from '../components/admin/XLSXImport'
import PhotoAdmin from '../components/admin/PhotoAdmin'
import EventManagement from '../components/admin/EventManagement'
import BulkWalletPassUpdate from '../components/admin/BulkWalletPassUpdate'
import HeadshotAdmin from '../components/admin/HeadshotAdmin'
import CompetitionManagement from '../components/admin/CompetitionManagement'
import ApiReference from '../components/admin/ApiReference'
import TeamManagement from '../components/admin/TeamManagement'

type AdminTab = 'dashboard' | 'users' | 'import' | 'events' | 'photos' | 'headshots' | 'competitions' | 'teams' | 'api-reference'

export default function AdminPage() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const isPhotoAdmin = user?.role === 'photo_admin'

  const tabs: { id: AdminTab; label: string; adminOnly: boolean }[] = [
    { id: 'dashboard', label: 'Overview', adminOnly: true },
    { id: 'users', label: 'Users', adminOnly: true },
    { id: 'import', label: 'Import', adminOnly: true },
    { id: 'events', label: 'Events', adminOnly: true },
    { id: 'photos', label: 'Photos', adminOnly: false },
    { id: 'headshots', label: 'Headshots', adminOnly: true },
    { id: 'competitions', label: 'Competitions', adminOnly: true },
    { id: 'teams', label: 'Teams', adminOnly: true },
    { id: 'api-reference', label: 'API Reference', adminOnly: true },
  ]

  const visibleTabs = tabs.filter(t => isAdmin || !t.adminOnly)
  
  // Determine default tab based on role, ensuring it's in visibleTabs
  const getDefaultTab = (): AdminTab => {
    if (isPhotoAdmin) return 'photos'
    if (isAdmin) return 'dashboard'
    return visibleTabs[0]?.id || 'photos'
  }

  const [activeTab, setActiveTab] = useState<AdminTab>(getDefaultTab())

  // Ensure activeTab is always valid for current user role
  const safeSetActiveTab = (tab: AdminTab) => {
    const isTabVisible = visibleTabs.some(t => t.id === tab)
    if (isTabVisible) {
      setActiveTab(tab)
    }
  }

  return (
    <ProtectedRoute>
      {/* ProtectedRoute ensures user exists, but add safety checks for role-based access */}
      {!user ? null : !isAdmin && !isPhotoAdmin ? (
        <Navigate to="/dashboard" replace />
      ) : (
        <>
          <Helmet><title>Admin - FWO Pilot Portal</title></Helmet>
          <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
            <AppHeader activePage="admin" />

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Admin Dashboard</h2>
                <p className="text-gray-600">Manage users, events, and photo competitions</p>
              </div>

              {/* Tab Navigation */}
              <div className="bg-white rounded-lg shadow mb-6">
                <div className="border-b border-gray-200">
                  <nav className="flex -mb-px overflow-x-auto">
                    {visibleTabs.map(tab => (
                      <button key={tab.id} onClick={() => safeSetActiveTab(tab.id)}
                        className={`px-6 py-4 text-sm font-medium whitespace-nowrap ${
                          activeTab === tab.id ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'
                        }`}>
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>

              {/* Tab Content */}
              {activeTab === 'dashboard' && isAdmin && (
                <div className="space-y-6">
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <button onClick={() => safeSetActiveTab('users')}
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left">
                        <div className="font-medium text-gray-900">Manage Users</div>
                        <div className="text-sm text-gray-600 mt-1">View and manage pilot accounts</div>
                      </button>
                      <button onClick={() => safeSetActiveTab('import')}
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left">
                        <div className="font-medium text-gray-900">Import Users</div>
                        <div className="text-sm text-gray-600 mt-1">Upload XLSX file to import pilots</div>
                      </button>
                      <button onClick={() => safeSetActiveTab('events')}
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left">
                        <div className="font-medium text-gray-900">Manage Events</div>
                        <div className="text-sm text-gray-600 mt-1">Create and manage competition events</div>
                      </button>
                      <button onClick={() => safeSetActiveTab('photos')}
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left">
                        <div className="font-medium text-gray-900">Photo Competition</div>
                        <div className="text-sm text-gray-600 mt-1">Manage themes and publish photos</div>
                      </button>
                      <button onClick={() => safeSetActiveTab('headshots')}
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left">
                        <div className="font-medium text-gray-900">Headshots</div>
                        <div className="text-sm text-gray-600 mt-1">Upload and link pilot headshot photos</div>
                      </button>
                      <button onClick={() => safeSetActiveTab('competitions')}
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left">
                        <div className="font-medium text-gray-900">Competitions</div>
                        <div className="text-sm text-gray-600 mt-1">Manage competition parameters and settings</div>
                      </button>
                      <button onClick={() => safeSetActiveTab('api-reference')}
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left">
                        <div className="font-medium text-gray-900">API Reference</div>
                        <div className="text-sm text-gray-600 mt-1">View all API routes and documentation</div>
                      </button>
                      <button onClick={() => safeSetActiveTab('teams')}
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left">
                        <div className="font-medium text-gray-900">Teams</div>
                        <div className="text-sm text-gray-600 mt-1">Manage pilot teams and assignments</div>
                      </button>
                    </div>
                  </div>

                  <BulkWalletPassUpdate />
                </div>
              )}

              {activeTab === 'users' && isAdmin && <UserManagement />}
              {activeTab === 'import' && isAdmin && <XLSXImport />}
              {activeTab === 'events' && isAdmin && <EventManagement />}
              {activeTab === 'photos' && <PhotoAdmin />}
              {activeTab === 'headshots' && isAdmin && <HeadshotAdmin />}
              {activeTab === 'competitions' && isAdmin && <CompetitionManagement />}
              {activeTab === 'teams' && isAdmin && <TeamManagement />}
              {activeTab === 'api-reference' && isAdmin && <ApiReference />}
            </main>
          </div>
        </>
      )}
    </ProtectedRoute>
  )
}
